import { IconDash } from "@/assets/icons/geist/IconDash";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { Button } from "@/components/flexkit/Button";
import { SearchInput } from "@/components/flexkit/SearchInput";
import { Checkbox } from "@/components/podkit/checkbox/Checkbox";
import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { Pill } from "@/components/podkit/status/Pill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/podkit/tables/Table";
import type { PlainGroupMembership } from "@/queries/group-queries";
import type { PlainOrganizationMember } from "@/queries/organization-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { MoreHorizontal } from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";
import { NoMembersYet } from "@/routes/settings/organization/members/NoMembersYet";

type GroupMembersTableProps = {
    memberships: PlainGroupMembership[];
    orgMembers: PlainOrganizationMember[];
    onRemoveMember: (memberIds: string[]) => void;
    onAddPeopleClick: () => void;
    canModifyMembers?: boolean;
};

export const GroupMembersTable: FC<GroupMembersTableProps> = ({
    memberships,
    orgMembers,
    onRemoveMember,
    onAddPeopleClick,
    canModifyMembers = true,
}) => {
    const { data: user } = useAuthenticatedUser();
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());

    // Helper function to map login provider to user-friendly label
    const getLoginProviderLabel = (loginProvider?: string): string => {
        switch (loginProvider) {
            case "magiclink":
                return "Email";
            case "github":
                return "GitHub";
            case "gitlab":
                return "GitLab";
            case "google":
                return "Google";
            case "custom":
                return "SSO";
            default:
                return "";
        }
    };

    // Map memberships to member details
    const members = useMemo(() => {
        if (!orgMembers.length || !memberships.length) return [];

        return memberships.map((membership) => {
            const orgMember = orgMembers.find((m) => m.userId === membership.subject?.id);
            return {
                id: membership.subject?.id || "",
                name: orgMember?.fullName || `Member ${membership.subject?.id?.slice(-6) || ""}`,
                email: orgMember?.email || "",
                avatarUrl: orgMember?.avatarUrl,
                role: orgMember?.role,
                membershipId: membership.id,
                isCurrentUser: membership.subject?.id === user?.id,
                loginProvider: getLoginProviderLabel(orgMember?.loginProvider),
            };
        });
    }, [memberships, orgMembers, user?.id]);

    // Filter members based on search query
    const filteredMembers = useMemo(() => {
        if (!searchQuery.trim()) return members;
        const query = searchQuery.toLowerCase();
        return members.filter(
            (member) => member.name.toLowerCase().includes(query) || member.email.toLowerCase().includes(query),
        );
    }, [members, searchQuery]);

    const handleMemberSelect = useCallback((memberId: string, selected: boolean) => {
        setSelectedMembers((prev) => {
            const newSet = new Set(prev);
            if (selected) {
                newSet.add(memberId);
            } else {
                newSet.delete(memberId);
            }
            return newSet;
        });
    }, []);

    const handleSelectAll = useCallback(
        (selected: boolean) => {
            if (selected) {
                setSelectedMembers(new Set(filteredMembers.map((m) => m.id)));
            } else {
                setSelectedMembers(new Set());
            }
        },
        [filteredMembers],
    );

    const handleRemoveMemberClick = useCallback(() => {
        if (selectedMembers.size === 0) {
            return;
        }
        onRemoveMember(Array.from(selectedMembers));
        setSelectedMembers(new Set());
    }, [selectedMembers, onRemoveMember]);

    return (
        <>
            {/* Search and Remove Bar */}
            <div className="mb-4 flex items-center gap-3">
                <div className="flex-1">
                    <span className="sr-only">Search group members by name, email, or role</span>
                    <SearchInput
                        className="max-w-none"
                        placeholder="Search by name, email, or role"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClear={() => setSearchQuery("")}
                    />
                </div>
                {canModifyMembers && (
                    <Button
                        variant="secondary"
                        size="md"
                        LeadingIcon={IconDash}
                        onClick={handleRemoveMemberClick}
                        disabled={selectedMembers.size === 0}
                        className="text-content-danger"
                        data-tracking-id="remove"
                    >
                        Remove
                    </Button>
                )}
            </div>

            {/* Members Table */}
            {filteredMembers.length === 0 ? (
                <NoMembersYet onAddPeopleClick={onAddPeopleClick} />
            ) : (
                <Table className="mt-4">
                    <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                        <TableRow className="h-8 text-base">
                            {canModifyMembers && (
                                <TableHead className="w-10 pl-3">
                                    <Checkbox
                                        checked={
                                            selectedMembers.size === filteredMembers.length && filteredMembers.length > 0
                                        }
                                        onCheckedChange={handleSelectAll}
                                    />
                                </TableHead>
                            )}
                            <TableHead className="pl-3">Name</TableHead>
                            <TableHead className="pl-3">Authenticated with</TableHead>
                            {canModifyMembers && <TableHead className="w-10" />}
                        </TableRow>
                    </TableHeader>
                    <TableBody className="border-t border-border-light text-base">
                        {filteredMembers.map((member) => (
                            <TableRow key={member.id} className="h-16 border-border-light">
                                {canModifyMembers && (
                                    <TableCell className="w-10">
                                        <Checkbox
                                            checked={selectedMembers.has(member.id)}
                                            onCheckedChange={(checked) => handleMemberSelect(member.id, !!checked)}
                                        />
                                    </TableCell>
                                )}
                                <TableCell className="max-w-xs">
                                    <div className="inline-flex items-center gap-3">
                                        <Avatar
                                            size={24}
                                            className="size-6 flex-shrink-0 rounded-full border-2 border-surface-glass bg-surface-glass ring-1 ring-border-light"
                                        >
                                            {member.avatarUrl && (
                                                <AvatarImage src={member.avatarUrl} alt={`${member.name}'s avatar`} />
                                            )}
                                            <AvatarFallback>
                                                <AvatarInitials name={member.name} size={24} />
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex items-center gap-2">
                                            <span className="text-base font-medium text-content-primary">
                                                {member.name}
                                            </span>
                                            {member.isCurrentUser && <Pill variant="success">me</Pill>}
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell className="w-40">
                                    <span className="text-base">{member.loginProvider}</span>
                                </TableCell>
                                {canModifyMembers && (
                                    <TableCell className="w-10">
                                        <DropdownActions
                                            button={
                                                <button className="rounded-md p-2 transition-colors hover:bg-surface-tertiary">
                                                    <MoreHorizontal className="size-4 text-content-tertiary" />
                                                </button>
                                            }
                                            align="end"
                                        >
                                            <DropdownMenuItem
                                                onClick={() => onRemoveMember([member.id])}
                                                variant="destructive"
                                                data-tracking-id="remove-from-group"
                                            >
                                                <IconTrash size="sm" className="mr-2" />
                                                Remove from group
                                            </DropdownMenuItem>
                                        </DropdownActions>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </>
    );
};
